/*!
 * Snowplow ecommerce tracking v4.5.0 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { __assign, __rest } from 'tslib';
import { dispatchToTrackersInCollection } from '@snowplow/browser-tracker-core';
import { buildSelfDescribingEvent } from '@snowplow/tracker-core';

var CART_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/cart/jsonschema/1-0-0';
var ECOMMERCE_ACTION_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/snowplow_ecommerce_action/jsonschema/1-0-2';
var CHECKOUT_STEP_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/checkout_step/jsonschema/1-0-0';
var PRODUCT_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/product/jsonschema/1-0-0';
var PAGE_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/page/jsonschema/1-0-0';
var USER_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/user/jsonschema/1-0-0';
var TRANSACTION_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/transaction/jsonschema/1-0-0';
var PROMO_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/promotion/jsonschema/1-0-0';
var REFUND_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/refund/jsonschema/1-0-0';
var TRANSACTION_ERROR_SCHEMA = 'iglu:com.snowplowanalytics.snowplow.ecommerce/transaction_error/jsonschema/1-0-0';

/**
 * Build an Ecommerce Action Event
 * For tracking ecommerce actions
 *
 * @param event - Contains the properties for Cart related events
 * @returns PayloadBuilder to be sent to {@link @snowplow/tracker-core#TrackerCore.track}
 */
function buildEcommerceActionEvent(event) {
    return buildSelfDescribingEvent({
        event: {
            schema: ECOMMERCE_ACTION_SCHEMA,
            data: removeEmptyProperties(__assign({}, event)),
        },
    });
}
/**
 * Returns a copy of a JSON with undefined and null properties removed
 *
 * @param event - Object to clean
 * @param exemptFields - Set of fields which should not be removed even if empty
 * @returns A cleaned copy of eventJson
 */
function removeEmptyProperties(event) {
    var ret = {};
    for (var k in event) {
        if (event[k] !== null && typeof event[k] !== 'undefined') {
            ret[k] = event[k];
        }
    }
    return ret;
}

var _trackers = {};
/* Scheduled to include Page and User */
var _context = {};
/**
 * Adds ecommerce tracking
 */
function SnowplowEcommercePlugin() {
    var trackerId;
    return {
        activateBrowserPlugin: function (tracker) {
            trackerId = tracker.id;
            _trackers[trackerId] = tracker;
            _context[trackerId] = [];
        },
        contexts: function () {
            return _context[trackerId] || [];
        },
    };
}
/**
 * Track a checkout step
 *
 * @param checkoutStep - The checkout step index and the attributes the user filled in.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackCheckoutStep(checkoutStep, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = checkoutStep.context, context = _a === void 0 ? [] : _a, timestamp = checkoutStep.timestamp, checkoutStepAttributes = __rest(checkoutStep, ["context", "timestamp"]);
    context.push({ schema: CHECKOUT_STEP_SCHEMA, data: __assign({}, checkoutStepAttributes) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'checkout_step' }), context, timestamp);
    });
}
/**
 * Track a product list view
 *
 * @param listView - The list name along with the products which were viewed by the visitor.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackProductListView(listView, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = listView.context, context = _a === void 0 ? [] : _a, timestamp = listView.timestamp, _b = listView.products, products = _b === void 0 ? [] : _b, name = listView.name;
    products.forEach(function (product) { return context.push({ schema: PRODUCT_SCHEMA, data: __assign({}, product) }); });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'list_view', name: name }), context, timestamp);
    });
}
/**
 * Track a product list click event
 *
 * @param listClick - The list name together with the information of the product which was clicked.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackProductListClick(listClick, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = listClick.context, context = _a === void 0 ? [] : _a, timestamp = listClick.timestamp, name = listClick.name, product = listClick.product;
    context.push({ schema: PRODUCT_SCHEMA, data: product });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'list_click', name: name }), context, timestamp);
    });
}
/**
 * Track a promotion view
 *
 * @param promotionView - The promotion viewed by the visitor.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackPromotionView(promotionView, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = promotionView.context, context = _a === void 0 ? [] : _a, timestamp = promotionView.timestamp, promotion = __rest(promotionView, ["context", "timestamp"]);
    context.push({ schema: PROMO_SCHEMA, data: __assign({}, promotion) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'promo_view' }), context, timestamp);
    });
}
/**
 * Track a promotion click
 *
 * @param promotionClick - The promotion selected by the visitor.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackPromotionClick(promotionClick, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = promotionClick.context, context = _a === void 0 ? [] : _a, timestamp = promotionClick.timestamp, promotion = __rest(promotionClick, ["context", "timestamp"]);
    context.push({ schema: PROMO_SCHEMA, data: __assign({}, promotion) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'promo_click' }), context, timestamp);
    });
}
/**
 * Track a product view/detail
 *
 * @param productView - The product which was viewed in a product detail page.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackProductView(productView, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = productView.context, context = _a === void 0 ? [] : _a, timestamp = productView.timestamp, product = __rest(productView, ["context", "timestamp"]);
    context.push({ schema: PRODUCT_SCHEMA, data: __assign({}, product) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'product_view' }), context, timestamp);
    });
}
/**
 * Track a product addition to cart
 *
 * @param cart - The product/s which was/were added to the cart and the total value of the cart after the product was added.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackAddToCart(cart, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = cart.context, context = _a === void 0 ? [] : _a, timestamp = cart.timestamp, _b = cart.products, products = _b === void 0 ? [] : _b, cartAttributes = __rest(cart, ["context", "timestamp", "products"]);
    products.forEach(function (product) { return context.push({ schema: PRODUCT_SCHEMA, data: __assign({}, product) }); });
    context.push({ schema: CART_SCHEMA, data: __assign({}, cartAttributes) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'add_to_cart' }), context, timestamp);
    });
}
/**
 * Track removal of products from cart
 *
 * @param cart - The product/s which were removed from the cart and the total value of the cart after the product was removed.
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackRemoveFromCart(cart, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = cart.context, context = _a === void 0 ? [] : _a, timestamp = cart.timestamp, _b = cart.products, products = _b === void 0 ? [] : _b, cartAttributes = __rest(cart, ["context", "timestamp", "products"]);
    products.forEach(function (product) { return context.push({ schema: PRODUCT_SCHEMA, data: __assign({}, product) }); });
    context.push({ schema: CART_SCHEMA, data: __assign({}, cartAttributes) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'remove_from_cart' }), context, timestamp);
    });
}
/**
 * Track a transaction event
 *
 * @param transaction - The transaction information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackTransaction(transaction, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var totalQuantity = 0;
    var _a = transaction.context, context = _a === void 0 ? [] : _a, timestamp = transaction.timestamp, _b = transaction.products, products = _b === void 0 ? [] : _b, transactionAttributes = __rest(transaction, ["context", "timestamp", "products"]);
    products.forEach(function (product) {
        /* If `total_quantity` is not provided, we calculate it from individual product quantities. */
        if (product.quantity) {
            totalQuantity += product.quantity;
        }
        context.push({ schema: PRODUCT_SCHEMA, data: product });
    });
    context.push({
        schema: TRANSACTION_SCHEMA,
        data: __assign({ total_quantity: totalQuantity || undefined }, transactionAttributes),
    });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'transaction' }), context, timestamp);
    });
}
/**
 * Track a refund event
 *
 * @param refund - The refund information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackRefund(refund, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = refund.context, context = _a === void 0 ? [] : _a, timestamp = refund.timestamp, _b = refund.products, products = _b === void 0 ? [] : _b, refundAttributes = __rest(refund, ["context", "timestamp", "products"]);
    products.forEach(function (product) { return context.push({ schema: PRODUCT_SCHEMA, data: product }); });
    context.push({ schema: REFUND_SCHEMA, data: __assign({}, refundAttributes) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'refund' }), context, timestamp);
    });
}
/**
 * Track a transaction error event
 *
 * @param transactionError - The transaction error information
 * @param trackers - The tracker identifiers which the event will be sent to
 */
function trackTransactionError(transactionError, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var _a = transactionError.context, context = _a === void 0 ? [] : _a, timestamp = transactionError.timestamp, transaction = transactionError.transaction, transactionErrorAttributes = __rest(transactionError, ["context", "timestamp", "transaction"]);
    context.push({
        schema: TRANSACTION_SCHEMA,
        data: __assign({}, transaction),
    });
    context.push({ schema: TRANSACTION_ERROR_SCHEMA, data: __assign({}, transactionErrorAttributes) });
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildEcommerceActionEvent({ type: 'trns_error' }), context, timestamp);
    });
}
/**
 * Set Ecommerce Page context
 *
 * @param context - The ecommerce page context to be stored
 * @param trackers - The tracker identifiers which the context will be stored
 */
function setPageType(context, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var type = context.type, language = context.language, locale = context.locale;
    trackers.forEach(function (trackerId) {
        if (_context[trackerId]) {
            _context[trackerId] = removeContext(_context[trackerId], PAGE_SCHEMA);
            _context[trackerId].push({
                schema: PAGE_SCHEMA,
                data: {
                    type: type,
                    language: language,
                    locale: locale,
                },
            });
        }
    });
}
/**
 * Set Ecommerce User context
 *
 * @param context - The ecommerce user context to be stored
 * @param trackers - The tracker identifiers which the context will be stored
 */
function setEcommerceUser(context, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    var id = context.id, is_guest = context.is_guest, email = context.email;
    trackers.forEach(function (trackerId) {
        if (_context[trackerId]) {
            _context[trackerId] = removeContext(_context[trackerId], USER_SCHEMA);
            _context[trackerId].push({
                schema: USER_SCHEMA,
                data: {
                    id: id,
                    is_guest: is_guest,
                    email: email,
                },
            });
        }
    });
}
/**
 * Return a new array with the context matching the `schemaToRemove` removed from the array.
 * Used in cases where we want only one type of the said context on the context array for a tracker.
 */
function removeContext(trackerContext, schemaToRemove) {
    return trackerContext.filter(function (context) { return context.schema !== schemaToRemove; });
}

function transformUAProductsToSPProducts(products, currency) {
    return products.map(function (product) {
        return {
            currency: currency,
            /* Id or name is required, but we in SP-plugin we require id to be present */
            id: product.id || product.name,
            price: Number(product.price),
            name: product.name,
            position: product.position,
            brand: product.brand,
            category: product.category,
            variant: product.variant,
            quantity: product.quantity,
        };
    });
}
function transformUAPromotionsToSPPromotions(promotions) {
    return promotions.map(function (promotion) { return ({
        name: promotion.name,
        slot: promotion.position,
        /* Id or name is required, but we in SP-plugin we require id to be present */
        id: promotion.id || promotion.name,
        creative_id: promotion.creative,
    }); });
}

var DEFAULT_PAYMENT_METHOD$1 = 'unknown';
function trackEnhancedEcommercePromoView(ecommerce) {
    var promotions = transformUAPromotionsToSPPromotions(ecommerce.promoView.promotions);
    /* Enhanced Ecommerce allows multiple promotions to be sent on a single event. */
    for (var i = 0; i < promotions.length; i++) {
        trackPromotionView(promotions[i]);
    }
}
function trackEnhancedEcommercePromoClick(ecommerce) {
    var promotion = transformUAPromotionsToSPPromotions(ecommerce.promoClick.promotions)[0];
    trackPromotionClick(promotion);
}
function trackEnhancedEcommerceAddToCart(ecommerce, opts) {
    var currency = (ecommerce.currencyCode || opts.currency);
    var products = transformUAProductsToSPProducts(ecommerce.add.products, currency);
    trackAddToCart({ products: products, total_value: opts.finalCartValue, currency: currency });
}
function trackEnhancedEcommerceRemoveFromCart(ecommerce, opts) {
    var currency = (ecommerce.currencyCode || opts.currency);
    var products = transformUAProductsToSPProducts(ecommerce.remove.products, currency);
    trackRemoveFromCart({ products: products, total_value: opts.finalCartValue, currency: currency });
}
function trackEnhancedEcommerceProductDetail(ecommerce, opts) {
    if (opts === void 0) { opts = {}; }
    var currency = (ecommerce.currencyCode || opts.currency);
    var products = transformUAProductsToSPProducts(ecommerce.detail.products, currency);
    trackProductView(__assign({}, products[0]));
}
function trackEnhancedEcommerceProductListView(ecommerce, opts) {
    if (opts === void 0) { opts = {}; }
    var currency = (ecommerce.currencyCode || opts.currency);
    var products = transformUAProductsToSPProducts(ecommerce.impressions, currency);
    trackProductListView({ name: ecommerce.impressions[0].list, products: products });
}
function trackEnhancedEcommerceProductListClick(ecommerce, opts) {
    if (opts === void 0) { opts = {}; }
    var currency = (ecommerce.currencyCode || opts.currency);
    var product = transformUAProductsToSPProducts(ecommerce.click.products, currency)[0];
    trackProductListClick({ name: ecommerce.click.actionField.list, product: product });
}
function trackEnhancedEcommerceCheckoutStep(ecommerce, checkoutOption) {
    trackCheckoutStep(__assign({ step: ecommerce.checkout.actionField.step }, checkoutOption));
}
function trackEnhancedEcommercePurchase(ecommerce, opts) {
    var currency = (ecommerce.currencyCode || opts.currency);
    var products = transformUAProductsToSPProducts(ecommerce.purchase.products, currency);
    trackTransaction({
        products: products,
        transaction_id: ecommerce.purchase.actionField.id,
        currency: currency,
        revenue: Number(ecommerce.purchase.actionField.revenue),
        payment_method: opts.paymentMethod || DEFAULT_PAYMENT_METHOD$1,
        tax: Number(ecommerce.purchase.actionField.tax) || undefined,
        shipping: Number(ecommerce.purchase.actionField.shipping) || undefined,
    });
}

function transformG4ItemsToSPProducts(_a, currency) {
    var items = _a.items, _b = _a.categorySeparator, categorySeparator = _b === void 0 ? '/' : _b;
    return items.map(function (ga4Item) {
        var item_category = ga4Item.item_category, item_category2 = ga4Item.item_category2, item_category3 = ga4Item.item_category3, item_category4 = ga4Item.item_category4, item_category5 = ga4Item.item_category5;
        var category = [item_category, item_category2, item_category3, item_category4, item_category5]
            .filter(Boolean)
            .join(categorySeparator);
        return {
            currency: currency,
            id: ga4Item.item_id,
            name: ga4Item.item_name,
            list_price: ga4Item.price,
            price: roundToTwo(ga4Item.price - (ga4Item.discount || 0)),
            position: ga4Item.index,
            brand: ga4Item.item_brand,
            category: category,
            variant: ga4Item.item_variant,
            quantity: ga4Item.quantity,
        };
    });
}
function transformGA4PromotionToSPPromotion(promotion) {
    var productIds = promotion.items.map(function (item) { return item.item_id; });
    return {
        id: promotion.promotion_id,
        name: promotion.promotion_name,
        creative_id: promotion.creative_name,
        slot: promotion.creative_slot,
        product_ids: productIds,
    };
}
function roundToTwo(num) {
    // @ts-ignore
    return +(Math.round(num + 'e+2') + 'e-2');
}

var DEFAULT_PAYMENT_METHOD = 'unknown';
function trackGA4ViewCart() {
    console.log('This function could be similarly tracked with the `setPageType` or `trackCheckoutStep` API.');
}
function trackGA4ViewPromotion(ecommerce) {
    var promotion = transformGA4PromotionToSPPromotion(ecommerce);
    trackPromotionView(promotion);
}
function trackGA4SelectPromotion(ecommerce) {
    var promotion = transformGA4PromotionToSPPromotion(ecommerce);
    trackPromotionClick(promotion);
}
function trackGA4AddToCart(ecommerce, opts) {
    var currency = (ecommerce.currency || opts.currency);
    var products = transformG4ItemsToSPProducts(ecommerce, currency);
    trackAddToCart({ products: products, total_value: opts.finalCartValue, currency: currency });
}
function trackGA4RemoveFromCart(ecommerce, opts) {
    var currency = (ecommerce.currency || opts.currency);
    var products = transformG4ItemsToSPProducts(ecommerce, currency);
    trackRemoveFromCart({ products: products, total_value: opts.finalCartValue, currency: currency });
}
function trackGA4ViewItem(ecommerce, opts) {
    if (opts === void 0) { opts = {}; }
    var currency = (ecommerce.currency || opts.currency);
    var products = transformG4ItemsToSPProducts(ecommerce, currency);
    trackProductView(__assign({}, products[0]));
}
function trackGA4ViewItemList(ecommerce, opts) {
    if (opts === void 0) { opts = {}; }
    var currency = (ecommerce.currency || opts.currency);
    var products = transformG4ItemsToSPProducts(ecommerce, currency);
    trackProductListView({ name: ecommerce.item_list_id || ecommerce.item_list_name, products: products });
}
function trackGA4SelectItem(ecommerce, opts) {
    if (opts === void 0) { opts = {}; }
    var currency = (ecommerce.currency || opts.currency);
    var product = transformG4ItemsToSPProducts(ecommerce, currency)[0];
    trackProductListClick({ name: ecommerce.item_list_id || ecommerce.item_list_name, product: product });
}
function trackGA4BeginCheckout(opts) {
    if (opts === void 0) { opts = {}; }
    trackCheckoutStep({ step: opts.step || 1 });
}
function trackGA4AddShippingInfo(ecommerce, opts) {
    trackCheckoutStep({ step: opts.step, delivery_method: ecommerce.shipping_tier });
}
function trackGA4AddPaymentOptions(ecommerce, opts) {
    trackCheckoutStep({ step: opts.step, payment_method: ecommerce.payment_type });
}
function trackGA4Transaction(ecommerce, opts) {
    var currency = (ecommerce.currency || opts.currency);
    var products = transformG4ItemsToSPProducts(ecommerce, currency);
    trackTransaction({
        products: products,
        transaction_id: ecommerce.transaction_id,
        currency: currency,
        revenue: ecommerce.value,
        payment_method: opts.paymentMethod || DEFAULT_PAYMENT_METHOD,
        tax: ecommerce.tax,
        shipping: ecommerce.shipping,
    });
}

export { SnowplowEcommercePlugin, setEcommerceUser, setPageType, trackAddToCart, trackCheckoutStep, trackEnhancedEcommerceAddToCart, trackEnhancedEcommerceCheckoutStep, trackEnhancedEcommerceProductDetail, trackEnhancedEcommerceProductListClick, trackEnhancedEcommerceProductListView, trackEnhancedEcommercePromoClick, trackEnhancedEcommercePromoView, trackEnhancedEcommercePurchase, trackEnhancedEcommerceRemoveFromCart, trackGA4AddPaymentOptions, trackGA4AddShippingInfo, trackGA4AddToCart, trackGA4BeginCheckout, trackGA4RemoveFromCart, trackGA4SelectItem, trackGA4SelectPromotion, trackGA4Transaction, trackGA4ViewCart, trackGA4ViewItem, trackGA4ViewItemList, trackGA4ViewPromotion, trackProductListClick, trackProductListView, trackProductView, trackPromotionClick, trackPromotionView, trackRefund, trackRemoveFromCart, trackTransaction, trackTransactionError };
//# sourceMappingURL=index.module.js.map
